// The 2D runtime scene: the tilemap's background/main layers composited into
// one pixel-perfect canvas texture on the XY plane (foreground is a second,
// transparent texture drawn in front of the player), a sprite (or quad)
// player with top-down WASD/arrow movement and tile collision, and an
// orthographic camera that follows the player in play mode. One engine, two
// projections.
import { OrthographicCamera } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

import type { Sprite2D, Tilemap2D, Tilemap2DEntity } from '../../content/schema/map';
import { INITIAL_PATROL_CLOCK, stepPatrolClock, type PatrolClock } from '../waypointPatrol';
import {
  entityFootprintCenter,
  entityFootprintSize,
  facingScaleX,
  isSpriteEmpty,
  PATROL_IDLE_ANIMATION,
  stepAnimationClock,
  tilemap2DNpcPatrolFrame,
  type AnimationClock,
  type Tilemap2DNpc,
  type Tilemap2DNpcFacing,
} from './npc2d';
import { spriteAnimationCells } from './sprite2d';
import { canvasTexture, drawSprite, drawSpriteCells, paintSpriteCellsAt } from './spriteCanvas';
import { buildSolidityGrid, findSpawn, stepTopDown, TILE_SIZE } from './topDown';

const PLAYER_SPEED = 4.5; // tiles per second
const PIXELS_PER_TILE = 48;

/** Paints one layer's non-null cells onto an already-sized context. A tile
 * with baked `art` is painted from that colour grid (scaled up to fill the
 * tile's `px` footprint); otherwise the tile falls back to its base colour
 * with the accent as a 2x2 checker, matching Pixel Studio's TileCell exactly
 * - so every tile authored before `art` existed keeps rendering exactly as
 * it did. A null cell is left untouched, so whatever was painted underneath
 * (a lower layer, or the caller's own base fill) shows through - this is
 * what lets background/main/foreground actually layer. */
function paintLayer(ctx: CanvasRenderingContext2D, map: Tilemap2D, cells: (string | null)[], px: number): void {
  const byId = new Map(map.tiles.map((t) => [t.id, t]));
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const tile = byId.get(cells[y * map.width + x] ?? '');
      if (!tile) continue;
      if (tile.art) {
        paintSpriteCellsAt(ctx, tile.art.size, tile.art.cells, x * px, y * px, px / tile.art.size);
        continue;
      }
      ctx.fillStyle = tile.a;
      ctx.fillRect(x * px, y * px, px, px);
      ctx.fillStyle = tile.b;
      const h = px / 2;
      ctx.fillRect(x * px, y * px, h, h);
      ctx.fillRect(x * px + h, y * px + h, h, h);
    }
  }
}

/** Draw the tilemap onto a canvas: the optional background layer first (so
 * it only shows through cells the main layer leaves empty), the main layer
 * on top, both compositing the same way the tile editor's own layer stack
 * does. The foreground layer is a separate texture (see
 * `drawForegroundLayer`), drawn in front of the player instead of behind it. */
function drawTilemap(map: Tilemap2D): HTMLCanvasElement {
  const px = 16; // texels per tile
  const canvas = document.createElement('canvas');
  canvas.width = map.width * px;
  canvas.height = map.height * px;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#0c0c0e';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  if (map.backgroundCells) paintLayer(ctx, map, map.backgroundCells, px);
  paintLayer(ctx, map, map.cells, px);
  return canvas;
}

/** The foreground layer's own texture, `null` when the layer is unpainted
 * (nothing to draw, and no point allocating a fully transparent mesh for
 * every map that doesn't use it). Left transparent everywhere else, unlike
 * the background/main composite, so the player and world sit visible
 * beneath whatever few cells this layer actually paints. */
function drawForegroundLayer(map: Tilemap2D): HTMLCanvasElement | null {
  if (!map.foregroundCells?.some((c) => c !== null)) return null;
  const px = 16;
  const canvas = document.createElement('canvas');
  canvas.width = map.width * px;
  canvas.height = map.height * px;
  const ctx = canvas.getContext('2d')!;
  paintLayer(ctx, map, map.foregroundCells, px);
  return canvas;
}

function TilemapMesh({ map }: { map: Tilemap2D }) {
  const texture = useMemo(() => canvasTexture(drawTilemap(map)), [map]);
  useEffect(() => () => texture.dispose(), [texture]);
  // World: x right, y down to match the painter; render with y flipped so
  // row 0 sits at the top, classic 2D.
  return (
    <mesh position={[(map.width * TILE_SIZE) / 2, -(map.height * TILE_SIZE) / 2, 0]}>
      <planeGeometry args={[map.width * TILE_SIZE, map.height * TILE_SIZE]} />
      <meshBasicMaterial map={texture} toneMapped={false} />
    </mesh>
  );
}

/** The foreground layer, rendered above the player (z 0.2, versus the
 * player's 0.1 and world entities' 0.05) so it can read as "in front of"
 * the world - overhanging structure, tall grass, anything meant to occlude
 * rather than sit under everything like the background layer does. */
function ForegroundTilemapMesh({ map }: { map: Tilemap2D }) {
  const canvas = useMemo(() => drawForegroundLayer(map), [map]);
  const texture = useMemo(() => (canvas ? canvasTexture(canvas) : null), [canvas]);
  useEffect(() => () => texture?.dispose(), [texture]);
  if (!texture) return null;
  return (
    <mesh position={[(map.width * TILE_SIZE) / 2, -(map.height * TILE_SIZE) / 2, 0.2]}>
      <planeGeometry args={[map.width * TILE_SIZE, map.height * TILE_SIZE]} />
      <meshBasicMaterial map={texture} transparent toneMapped={false} />
    </mesh>
  );
}

function useKeys(): { current: Set<string> } {
  const keys = useRef(new Set<string>());
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;
      keys.current.add(e.code);
    };
    const up = (e: KeyboardEvent) => keys.current.delete(e.code);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);
  return keys;
}

const GAMEPAD_STICK_DEADZONE = 0.2;

/** The Studio editor's own "Input map" settings panel
 * (`src/studio/editor/editor-panels.tsx`) has always listed "Move: WASD /
 * Stick" as a live binding for every project, 2D included - this was the
 * editor promising a capability this scene never actually had (this
 * scene's own `useKeys` never read a gamepad at all; only
 * `Platformer2DScene` did). Reads every connected pad's left stick past a
 * deadzone, returning the first one actively deflected (not necessarily
 * pad index 0 - with more than one pad connected, whichever one the player
 * is actually touching wins), or `null` (falls back to keyboard) when none
 * is - the same "an idle, connected controller never steals input from a
 * keyboard player" policy every other gamepad-aware input reader in this
 * workspace already uses. No button/analog-speed handling: this scene's
 * own `Player` has no button-driven action of its own to bind (the same
 * panel's own hint already states "Interact and Primary action have no 2D
 * top-down implementation yet"), and using the stick's raw angle only (not
 * its magnitude) for direction keeps gamepad and keyboard movement feeling
 * identical - full speed in the pressed direction, matching every existing
 * keyboard tile of this map exactly. */
function readGamepadMove(): { x: number; y: number } | null {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return null;
  const pads = navigator.getGamepads();
  for (const gp of pads) {
    if (!gp || !gp.connected) continue;
    const x = Math.abs(gp.axes[0] ?? 0) > GAMEPAD_STICK_DEADZONE ? (gp.axes[0] ?? 0) : 0;
    const y = Math.abs(gp.axes[1] ?? 0) > GAMEPAD_STICK_DEADZONE ? (gp.axes[1] ?? 0) : 0;
    if (x !== 0 || y !== 0) return { x, y };
  }
  return null;
}

/** The frame-resolved state `Player` hands back to `onFrameResolved` once
 * movement has settled - `position` is the raw, un-tile-floored world
 * position (matching `onMove`'s own values), `velocity` is the resolved
 * (post-`beforeMovementResolve`) input direction actually applied this
 * frame. Deliberately much smaller than `sstk-furrow`'s own
 * `TopDownCatPlayer`-equivalent `TopDownCatFrameInfo`: this component has
 * no locomotion state machine (no posture, no per-frame result) to report,
 * only movement itself. */
export interface Tilemap2DFrameInfo {
  position: { x: number; y: number };
  velocity: { x: number; y: number };
  dt: number;
}

interface PlayerProps {
  map: Tilemap2D;
  follow: boolean;
  onMove?: (x: number, y: number) => void;
  /** Called once per frame with the raw keyboard-or-gamepad move vector,
   * before it is normalised and scaled by `PLAYER_SPEED` - a chance to
   * return an override (e.g. zero it while some scene-owned system says
   * movement is locked). Defaults to identity. Mirrors `sstk-furrow`'s own
   * `TopDownCatPlayer.beforeMovementResolve`, the same shape proven there
   * first (`docs/production/player-unification-plan.md` in that repo) -
   * ported here once porting task confirmed it as a genuinely generic
   * need, not just Furrow's own Combat scene's. */
  beforeMovementResolve?: (vel: { x: number; y: number }) => { x: number; y: number };
  /** Called once per frame after movement has resolved, with the player's
   * resolved position, the velocity actually applied, and `dt`. The
   * `onFrameResolved` half of the same extension surface
   * `beforeMovementResolve` documents. */
  onFrameResolved?: (info: Tilemap2DFrameInfo) => void;
}

function Player({ map, follow, onMove, beforeMovementResolve, onFrameResolved }: PlayerProps) {
  const grid = useMemo(() => buildSolidityGrid(map), [map]);
  const spawn = useMemo(() => findSpawn(map, grid), [map, grid]);
  const pos = useRef({ x: spawn.x, y: spawn.y });
  const meshRef = useRef<THREE.Mesh>(null);
  const keys = useKeys();
  const { camera } = useThree();

  // An empty sprite (no painted pixels) falls back to the visible quad. The
  // flipbook state (canvas, texture) lives alongside the mesh so playback
  // survives re-renders without re-allocating.
  const flipbook = useMemo(() => {
    const sprite = map.playerSprite;
    if (!sprite || !sprite.cells.some((cell) => cell !== null)) return null;
    const canvas = drawSprite(sprite);
    return { sprite, canvas, texture: canvasTexture(canvas) };
  }, [map.playerSprite]);
  const texture = flipbook?.texture ?? null;
  useEffect(() => () => texture?.dispose(), [texture]);
  // Top-down has no physics state machine (no jump/dash/etc.), so its pose
  // vocabulary is its own plain string, not the platformer's PoseId union -
  // that type is scoped to Platformer2DScene's own state machine.
  const animStateRef = useRef<string>('idle');
  const animElapsedRef = useRef(0);

  useFrame((_, dtRaw) => {
    const dt = Math.min(dtRaw, 0.05);
    const k = keys.current;
    const keyboardVel = {
      x: (k.has('KeyD') || k.has('ArrowRight') ? 1 : 0) - (k.has('KeyA') || k.has('ArrowLeft') ? 1 : 0),
      y: (k.has('KeyS') || k.has('ArrowDown') ? 1 : 0) - (k.has('KeyW') || k.has('ArrowUp') ? 1 : 0),
    };
    // An idle, connected controller never steals input from a keyboard
    // player already moving - `readGamepadMove` returns null unless the
    // stick is actively deflected past its own deadzone.
    const vel = readGamepadMove() ?? keyboardVel;
    const resolved = beforeMovementResolve ? beforeMovementResolve(vel) : vel;
    const length = Math.hypot(resolved.x, resolved.y) || 1;
    // The actual applied velocity (tiles/second), not `resolved` itself -
    // `resolved` is the raw input direction (keyboard's diagonal magnitude
    // is ~1.41, gamepad's is the stick's own raw deflection), never what
    // `stepTopDown` was actually given below. `onFrameResolved`'s own
    // `velocity` field promises "the velocity actually applied this frame",
    // so it must be this, not the pre-scale input.
    const appliedVelocity = { x: (resolved.x / length) * PLAYER_SPEED, y: (resolved.y / length) * PLAYER_SPEED };
    pos.current = stepTopDown(pos.current, appliedVelocity, grid, dt);
    if (meshRef.current) {
      meshRef.current.position.set(pos.current.x, -pos.current.y, 0.1);
    }
    if (follow) {
      camera.position.x = pos.current.x;
      camera.position.y = -pos.current.y;
    }
    onFrameResolved?.({ position: { x: pos.current.x, y: pos.current.y }, velocity: appliedVelocity, dt });
    // Flipbook playback: named clips first (a sprite authored with 'walk'/
    // 'idle' clips animates correctly here exactly as it does in
    // Platformer2DScene), falling back to the flat flipbook otherwise -
    // spriteAnimationCells already handles that fallback internally.
    if (flipbook) {
      const targetAnim = resolved.x !== 0 || resolved.y !== 0 ? 'walk' : 'idle';
      if (animStateRef.current !== targetAnim) {
        animStateRef.current = targetAnim;
        animElapsedRef.current = 0;
      } else {
        // The unclamped frame delta, deliberately - this line predates the
        // `beforeMovementResolve`/`onFrameResolved` hook and only ever used
        // the raw `dtRaw` (clamping was previously applied only at the
        // `stepTopDown` call site, not here). Reusing the new clamped `dt`
        // for this too would be a real, undocumented behaviour change for
        // every existing caller: after a frame hitch or a backgrounded tab,
        // the animation clock would now stall and catch up over several
        // frames instead of jumping forward with real elapsed time, exactly
        // as `Platformer2DScene` and the rest of the engine already treat
        // animation clocks.
        animElapsedRef.current += dtRaw;
      }
      const activeCells = spriteAnimationCells(flipbook.sprite, animStateRef.current, animElapsedRef.current);
      drawSpriteCells(flipbook.canvas, flipbook.sprite.size, activeCells);
      flipbook.texture.needsUpdate = true;
    }
    onMove?.(pos.current.x, pos.current.y);
  });

  return (
    <mesh ref={meshRef} position={[spawn.x, -spawn.y, 0.1]}>
      <planeGeometry args={[TILE_SIZE * 0.8, TILE_SIZE * 0.8]} />
      {texture ? (
        <meshBasicMaterial map={texture} transparent toneMapped={false} />
      ) : (
        <meshBasicMaterial color="#71b4fe" toneMapped={false} />
      )}
    </mesh>
  );
}

/** A single placed sprite (prop, NPC, decoration): a static textured quad at
 * its cell, matching the player sprite's rendering exactly but with no
 * flipbook playback or movement - the visual placement half of a 2D
 * "object instance"; collision and behaviour are not part of this yet (see
 * `Tilemap2DEntity`'s doc comment). An unpainted sprite (placed before it has
 * any art) falls back to a visible quad instead of a fully transparent one,
 * same as the player does - a placed-but-invisible entity would look like the
 * placement silently failed. */
function WorldEntity({ x, y, sprite, footprint }: { x: number; y: number; sprite: Sprite2D; footprint?: Tilemap2DEntity['footprint'] }) {
  const isEmpty = !sprite.cells.some((cell) => cell !== null);
  const texture = useMemo(() => (isEmpty ? null : canvasTexture(drawSprite(sprite))), [isEmpty, sprite]);
  useEffect(() => () => texture?.dispose(), [texture]);
  const center = entityFootprintCenter(x, y, footprint, TILE_SIZE);
  const size = entityFootprintSize(footprint, TILE_SIZE);
  return (
    <mesh position={[center.x, -center.y, 0.05]}>
      <planeGeometry args={[size.width, size.height]} />
      {texture ? (
        <meshBasicMaterial map={texture} transparent toneMapped={false} />
      ) : (
        <meshBasicMaterial color="#e8806c" toneMapped={false} />
      )}
    </mesh>
  );
}

/** Every placed entity whose `spriteId` resolves to a real sprite in the
 * map's library - a dangling reference (the sprite was deleted after being
 * placed) is skipped rather than crashing the scene. Excludes any entity
 * carrying an authored `patrol` (see `PatrollingEntities`): those render as
 * live, moving NPCs instead, and rendering both here too would double them
 * up - one frozen at its placement cell, one actually walking. */
/** Exported so `Platformer2DScene` can render the same placed props/NPCs
 * without duplicating this component - both 2D modes share one tilemap
 * `entities` concept (see `Tilemap2DEntity`'s own doc comment). */
export function WorldEntities({ map }: { map: Tilemap2D }) {
  const byId = useMemo(() => new Map((map.sprites ?? []).map((s) => [s.id, s])), [map.sprites]);
  const entities = (map.entities ?? []).filter((e) => !e.patrol);
  if (entities.length === 0) return null;
  return (
    <>
      {entities.map((e) => {
        const sprite = byId.get(e.spriteId);
        return sprite ? <WorldEntity key={e.id} x={e.x} y={e.y} sprite={sprite} footprint={e.footprint} /> : null;
      })}
    </>
  );
}

/** This frame's position/animation/facing for a live NPC: the consumer's own
 * `x`/`y`/`animation`/`facing` when it has no `patrol`, exactly as before
 * `patrol` existed; a patrol's own `patrolSampleAt`-derived frame otherwise,
 * ignoring the consumer's `x`/`y`/`animation` (which then only seed the
 * mesh's position on first paint, before any frame has run). */
function resolveNpcFrame(
  npc: Tilemap2DNpc,
  patrolClock: PatrolClock,
): { x: number; y: number; animation: string; facing?: Tilemap2DNpcFacing } {
  if (!npc.patrol) return { x: npc.x, y: npc.y, animation: npc.animation, facing: npc.facing };
  return tilemap2DNpcPatrolFrame(npc.patrol, patrolClock.elapsedMs);
}

/** A single live NPC: WorldEntity's externally-positioned placement plus
 * Player's own per-frame flipbook playback and a facing-driven horizontal
 * flip, the combination neither models alone - a live NPC has WorldEntity's
 * "position and sprite come from outside" shape and Player's "actually
 * animates every frame" behaviour at once. Without a `patrol`, position and
 * facing stay plain JSX props, recomputed whenever the consumer re-renders
 * `npcs` (no local physics state to own between renders, unlike Player) -
 * only the animation clock genuinely needs `useFrame` in that case, since
 * elapsed-time-in-clip must keep advancing regardless of how often the
 * consumer's own npcs array changes. A `patrol` reverses that: the consumer
 * supplies no per-frame position at all, so `useFrame` drives the mesh
 * imperatively instead, the same way `Player`'s own physics-driven position
 * already does. An unpainted sprite falls back to a visible quad, same
 * reasoning as WorldEntity: a placed-but-invisible NPC would look like a bug,
 * not an empty sprite. */
function Npc({ npc }: { npc: Tilemap2DNpc }) {
  const { sprite } = npc;
  const isEmpty = useMemo(() => isSpriteEmpty(sprite), [sprite]);
  const flipbook = useMemo(() => {
    if (isEmpty) return null;
    const canvas = drawSprite(sprite);
    return { canvas, texture: canvasTexture(canvas) };
  }, [isEmpty, sprite]);
  const texture = flipbook?.texture ?? null;
  useEffect(() => () => texture?.dispose(), [texture]);

  const meshRef = useRef<THREE.Mesh>(null);
  const clockRef = useRef<AnimationClock>({ name: npc.animation, elapsed: 0 });
  const patrolClockRef = useRef<PatrolClock>(INITIAL_PATROL_CLOCK);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- first-paint seed only; useFrame drives every frame after.
  const initialFrame = useMemo(() => resolveNpcFrame(npc, INITIAL_PATROL_CLOCK), []);

  useFrame((_, dt) => {
    if (npc.patrol) patrolClockRef.current = stepPatrolClock(patrolClockRef.current, dt);
    const frame = resolveNpcFrame(npc, patrolClockRef.current);
    clockRef.current = stepAnimationClock(clockRef.current, frame.animation, dt);
    if (flipbook) {
      const cells = spriteAnimationCells(sprite, clockRef.current.name, clockRef.current.elapsed);
      drawSpriteCells(flipbook.canvas, sprite.size, cells);
      flipbook.texture.needsUpdate = true;
    }
    if (meshRef.current) {
      meshRef.current.position.set((frame.x + 0.5) * TILE_SIZE, -(frame.y + 0.5) * TILE_SIZE, 0.075);
      meshRef.current.scale.x = facingScaleX(frame.facing);
    }
  });

  return (
    <mesh
      ref={meshRef}
      position={[(initialFrame.x + 0.5) * TILE_SIZE, -(initialFrame.y + 0.5) * TILE_SIZE, 0.075]}
      scale={[facingScaleX(initialFrame.facing), 1, 1]}
    >
      <planeGeometry args={[TILE_SIZE * 0.8, TILE_SIZE * 0.8]} />
      {texture ? (
        <meshBasicMaterial map={texture} transparent toneMapped={false} />
      ) : (
        <meshBasicMaterial color="#c98be0" toneMapped={false} />
      )}
    </mesh>
  );
}

/** Every consumer-supplied NPC, keyed by id. Mirrors WorldEntities: an
 * empty or undefined list renders nothing. */
function Npcs({ npcs }: { npcs?: Tilemap2DNpc[] }) {
  const list = npcs ?? [];
  if (list.length === 0) return null;
  return (
    <>
      {list.map((npc) => (
        <Npc key={npc.id} npc={npc} />
      ))}
    </>
  );
}

/** Every placed entity carrying an authored `patrol`, rendered as a live,
 * self-driving `Npc` instead of a static `WorldEntity` - this is what makes
 * a patrol authored through Pixel Studio actually walk in Preview/Play with
 * no bespoke per-frame code from whatever hosts this scene (unlike `npcs`,
 * which stays entirely consumer-driven). A dangling `spriteId` is skipped,
 * the same convention `WorldEntities` already follows. */
/** Exported alongside `WorldEntities` for `Platformer2DScene`'s reuse. */
export function PatrollingEntities({ map }: { map: Tilemap2D }) {
  const byId = useMemo(() => new Map((map.sprites ?? []).map((s) => [s.id, s])), [map.sprites]);
  const entities = (map.entities ?? []).filter((e) => e.patrol);
  if (entities.length === 0) return null;
  return (
    <>
      {entities.map((e) => {
        const sprite = byId.get(e.spriteId);
        if (!sprite || !e.patrol) return null;
        const npc: Tilemap2DNpc = { id: e.id, x: e.x, y: e.y, sprite, animation: PATROL_IDLE_ANIMATION, patrol: e.patrol };
        return <Npc key={e.id} npc={npc} />;
      })}
    </>
  );
}

export interface Tilemap2DSceneProps {
  map: Tilemap2D;
  /** 'play' moves the player and follows the camera; 'edit' frames the map. */
  mode: 'play' | 'edit';
  /** Per-frame player position in tile coordinates (play mode). */
  onPlayerMove?: (x: number, y: number) => void;
  /** Live NPCs to render this frame - position, sprite, animation and
   * facing are entirely consumer-owned (e.g. resolved by a game's own
   * schedule/AI and possibly broadcast over its own server protocol); the
   * engine only draws them, exactly as Player's own position is already
   * entirely input/consumer driven. Omitted or empty renders nothing. */
  npcs?: Tilemap2DNpc[];
  /** See `Player`'s own prop of the same name. Ignored outside `mode:
   * 'play'`. */
  beforeMovementResolve?: (vel: { x: number; y: number }) => { x: number; y: number };
  /** See `Player`'s own prop of the same name. Ignored outside `mode:
   * 'play'`. */
  onFrameResolved?: (info: Tilemap2DFrameInfo) => void;
}

/** Mount inside a Canvas: orthographic 2D world for the given tilemap. */
export function Tilemap2DScene({ map, mode, onPlayerMove, npcs, beforeMovementResolve, onFrameResolved }: Tilemap2DSceneProps) {
  const [size] = useState(() => ({ w: map.width, h: map.height }));
  const centre: [number, number, number] = [(size.w * TILE_SIZE) / 2, -(size.h * TILE_SIZE) / 2, 10];
  return (
    <>
      <OrthographicCamera makeDefault position={centre} zoom={PIXELS_PER_TILE} near={0.1} far={100} />
      <ambientLight intensity={1} />
      <TilemapMesh map={map} />
      <WorldEntities map={map} />
      <PatrollingEntities map={map} />
      <Npcs npcs={npcs} />
      {mode === 'play' && (
        <Player map={map} follow onMove={onPlayerMove} beforeMovementResolve={beforeMovementResolve} onFrameResolved={onFrameResolved} />
      )}
      <ForegroundTilemapMesh map={map} />
    </>
  );
}
